import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { PreAlertService } from '../../services/pre-alert.service';
import { AuthService } from '../../services/auth.service';
import { Observable } from 'rxjs';
import {
  PreAlertDetail as PreAlertDetailModel,
  CartonMapping,
  ExportHistoryItem,
  CartonDisplayData,
  ShopeeOrder,
  ApiResponse
} from '../../../models/pre-alert.models';
import { DeleteConfirmationComponent } from '../modals/delete-confirmation/delete-confirmation.component';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideDownload,
  lucidePencil,
  lucideTrash2,
  lucideRefreshCw,
  lucideWarehouse,
  lucideTruck,
  lucideEye,
  lucidePackage,
  lucideFileText,
  lucideX
} from '@ng-icons/lucide';
import {
  PageHeaderComponent,
  StatusBadgeComponent,
  InlineAlertComponent,
  EmptyStateComponent,
  SkeletonComponent
} from '@shared/ui/components';
import type { StatusBadgeVariant } from '@shared/ui/components';

@Component({
  selector: 'app-pre-alert-detail',
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    DeleteConfirmationComponent,
    ...HlmCardImports,
    HlmButton,
    HlmIcon,
    NgIcon,
    PageHeaderComponent,
    StatusBadgeComponent,
    InlineAlertComponent,
    EmptyStateComponent,
    SkeletonComponent
  ],
  providers: [
    provideIcons({
      lucideArrowLeft,
      lucideDownload,
      lucidePencil,
      lucideTrash2,
      lucideRefreshCw,
      lucideWarehouse,
      lucideTruck,
      lucideEye,
      lucidePackage,
      lucideFileText,
      lucideX
    })
  ],
  templateUrl: './pre-alert-detail.html',
  styleUrl: './pre-alert-detail.css'
})
export class PreAlertDetail implements OnInit {
  preAlert: PreAlertDetailModel | null = null;
  isLoading = false;
  error: string | null = null;
  ladingBill: string | null = null;
  
  // Export functionality state
  isExportingLMWarehouse = false;
  isExportingTruckLoading = false;
  exportHistory: ExportHistoryItem[] = [];
  isLoadingExportHistory = false;

  // New carton display functionality
  cartons: CartonDisplayData[] = [];

  // Delete confirmation modal state
  showDeleteConfirmation = false;
  isDeleting = false;

  // Edit modal state
  showEditModal = false;
  isEditing = false;
  editForm: FormGroup | null = null;

  // Carton delete state
  cartonToDelete: CartonDisplayData | null = null;
  showCartonDeleteConfirmation = false;
  isDeletingCarton = false;

  // Role-based access control
  isShopeeChecker$!: Observable<boolean>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private preAlertService: PreAlertService,
    private formBuilder: FormBuilder,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Initialize role-based access control
    this.isShopeeChecker$ = this.authService.isShopeeChecker$;

    this.route.paramMap.subscribe(params => {
      const ladingBill = params.get('id'); // Using 'id' as the route parameter but it contains lading bill
      if (ladingBill) {
        this.ladingBill = ladingBill;
        this.loadPreAlertDetail();
        this.loadExportHistory();
      }
    });
  }

  private loadPreAlertDetail(): void {
    if (!this.ladingBill) return;

    this.isLoading = true;
    this.error = null;

    this.preAlertService.getPreAlertByLadingBill(this.ladingBill).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.retcode === 0 && response.data) {
          this.preAlert = response.data as PreAlertDetailModel;
          // Use cartons from the response instead of making a separate API call
          this.cartons = this.transformCartonsData(this.preAlert.cartons || []);
          console.log(`Loaded pre-alert with ${this.cartons.length} cartons`);
        } else {
          this.error = response.message || 'Failed to load pre-alert details';
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.error = 'Failed to load pre-alert details';
        console.error('Error loading pre-alert:', error);
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/dashboard/pre-alerts']);
  }

  refresh(): void {
    this.loadPreAlertDetail();
    this.loadExportHistory();
  }

  // Helper methods
  formatDate(date: Date | string): string {
    return this.preAlertService.formatDate(date);
  }

  formatWeight(weight: number): string {
    return this.preAlertService.formatWeight(weight);
  }

  getDaysUntilETA(eta: Date | string): number {
    return this.preAlertService.getDaysUntilETA(eta);
  }

  getETAStatus(eta: Date | string): string {
    return this.preAlertService.getETAStatus(eta);
  }

  getETAStatusClass(eta: Date | string): string {
    const status = this.getETAStatus(eta);
    switch (status) {
      case 'overdue': return 'text-danger';
      case 'today': return 'text-warning';
      case 'upcoming': return 'text-info';
      default: return 'text-success';
    }
  }

  getETAStatusBadgeClass(eta: Date | string): string {
    const status = this.getETAStatus(eta);
    switch (status) {
      case 'overdue': return 'danger';
      case 'today': return 'warning';
      case 'upcoming': return 'info';
      default: return 'success';
    }
  }

  getStatusBadgeClass(status: string): string {
    return this.preAlertService.getStatusBadgeClass(status);
  }

  trackByCartonMapping(index: number, mapping: CartonMapping): string {
    return mapping.cartonNumber;
  }

  // Export functionality methods
  exportLMWarehouse(): void {
    if (!this.ladingBill || this.isExportingLMWarehouse) return;

    this.isExportingLMWarehouse = true;
    this.error = null;

    this.preAlertService.exportLMWarehouse(this.ladingBill).subscribe({
      next: (blob) => {
        const filename = this.preAlertService.generateExportFilename(this.ladingBill!, 'LM-Warehouse');
        this.preAlertService.downloadFile(blob, filename);
        this.isExportingLMWarehouse = false;
        this.loadExportHistory(); // Refresh export history
      },
      error: (error) => {
        this.error = 'Failed to export LM Warehouse data';
        this.isExportingLMWarehouse = false;
        console.error('Export LM Warehouse error:', error);
      }
    });
  }

  exportTruckLoading(): void {
    if (!this.ladingBill || this.isExportingTruckLoading) return;

    this.isExportingTruckLoading = true;
    this.error = null;

    this.preAlertService.exportTruckLoading(this.ladingBill).subscribe({
      next: (blob) => {
        const filename = this.preAlertService.generateExportFilename(this.ladingBill!, 'Truck-Loading');
        this.preAlertService.downloadFile(blob, filename);
        this.isExportingTruckLoading = false;
        this.loadExportHistory(); // Refresh export history
      },
      error: (error) => {
        this.error = 'Failed to export Truck Loading data';
        this.isExportingTruckLoading = false;
        console.error('Export Truck Loading error:', error);
      }
    });
  }

  private loadExportHistory(): void {
    if (!this.ladingBill) return;

    this.isLoadingExportHistory = true;

    this.preAlertService.getExportHistory(this.ladingBill).subscribe({
      next: (response) => {
        this.isLoadingExportHistory = false;
        if (response.retcode === 0 && response.data) {
          this.exportHistory = response.data;
        } else {
          this.exportHistory = [];
        }
      },
      error: (error) => {
        this.isLoadingExportHistory = false;
        this.exportHistory = [];
        console.error('Error loading export history:', error);
      }
    });
  }

  getExportStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'processing': return 'warning';
      default: return 'secondary';
    }
  }

  trackByExportHistory(index: number, item: ExportHistoryItem): string {
    return item.id;
  }

  // Transform cartons data from API response to display format
  private transformCartonsData(cartons: CartonMapping[]): CartonDisplayData[] {
    return cartons.map(carton => ({
      cartonNo: carton.cartonNumber,
      uniqueId: carton.cartonNumber, // Using cartonNumber as uniqueId since that's what we have
      parcelCount: 1, // Each carton mapping represents one item
      parcels: [], // No detailed parcel data available from carton mappings
      weight: undefined, // Weight info not available in carton mappings
      volume: undefined, // Volume info not available in carton mappings
      isScanned: carton.isScanned,
      scannedAtUtc: carton.scannedAtUtc,
      scannedBy: carton.scannedBy
    }));
  }

  trackByCartonDisplay(index: number, carton: CartonDisplayData): string {
    return carton.uniqueId;
  }

  getTotalParcelCount(): number {
    return this.cartons.reduce((sum, c) => sum + c.parcelCount, 0);
  }

  // Delete confirmation functionality
  openDeleteConfirmation(): void {
    console.log('🗑️ openDeleteConfirmation called, preAlert:', this.preAlert);
    if (this.preAlert) {
      console.log('✅ Setting showDeleteConfirmation to true');
      this.showDeleteConfirmation = true;
      console.log('🔍 showDeleteConfirmation is now:', this.showDeleteConfirmation);
    } else {
      console.log('❌ No preAlert found, cannot show delete confirmation');
    }
  }

  onDeleteConfirmed(): void {
    if (!this.preAlert || this.isDeleting) return;

    this.isDeleting = true;
    this.error = null;

    this.preAlertService.deletePreAlert(this.preAlert.ladingBill).subscribe({
      next: (response) => {
        this.isDeleting = false;
        this.showDeleteConfirmation = false;
        
        if (response.retcode === 0) {
          // Successfully deleted, navigate back to list
          this.router.navigate(['/dashboard/pre-alerts']);
        } else {
          this.error = response.message || 'Failed to delete pre-alert';
        }
      },
      error: (error) => {
        this.isDeleting = false;
        this.showDeleteConfirmation = false;
        this.error = 'Failed to delete pre-alert';
        console.error('Delete error:', error);
      }
    });
  }

  onDeleteCancelled(): void {
    this.showDeleteConfirmation = false;
  }

  // Edit functionality
  openEditModal(): void {
    if (!this.preAlert) return;
    
    this.editForm = this.formBuilder.group({
      ladingBill: [{value: this.preAlert.ladingBill, disabled: true}],
      vessel: [this.preAlert.vessel, [Validators.required]],
      voyage: [this.preAlert.voyage, [Validators.required]],
      eta: [this.formatDateForInput(this.preAlert.eta), [Validators.required]],
      etd: [this.formatDateForInput(this.preAlert.etd), [Validators.required]],
      portOfLoading: [this.preAlert.portOfLoading, [Validators.required]],
      portOfDischarge: [this.preAlert.portOfDischarge, [Validators.required]],
      totalCartons: [this.preAlert.totalCartons, [Validators.required, Validators.min(1)]],
      totalWeight: [this.preAlert.totalWeight, [Validators.required, Validators.min(0.1)]],
      totalVolume: [this.preAlert.totalVolume, [Validators.required, Validators.min(0.01)]],
      remarks: [this.preAlert.remarks || '']
    });
    
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editForm = null;
  }

  savePreAlert(): void {
    if (!this.editForm || this.editForm.invalid || !this.preAlert) return;
    
    this.isEditing = true;
    
    const formValue = this.editForm.value;
    const updateData = {
      ladingBill: this.preAlert.ladingBill,
      vessel: formValue.vessel,
      voyage: formValue.voyage,
      eta: new Date(formValue.eta).toISOString(),
      etd: new Date(formValue.etd).toISOString(),
      portOfLoading: formValue.portOfLoading,
      portOfDischarge: formValue.portOfDischarge,
      totalCartons: parseInt(formValue.totalCartons),
      totalWeight: parseFloat(formValue.totalWeight),
      totalVolume: parseFloat(formValue.totalVolume),
      remarks: formValue.remarks || ''
    };

    this.preAlertService.updatePreAlert(updateData).subscribe({
      next: (response: ApiResponse) => {
        this.isEditing = false;
        if (response.retcode === 0) {
          this.closeEditModal();
          this.loadPreAlertDetail(); // Refresh data
        } else {
          this.error = response.message || 'Failed to update pre-alert';
        }
      },
      error: (error: any) => {
        this.isEditing = false;
        this.error = 'Failed to update pre-alert';
        console.error('Update error:', error);
      }
    });
  }

  private formatDateForInput(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  hasFieldError(fieldName: string): boolean {
    const field = this.editForm?.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.editForm?.get(fieldName);
    if (field && field.errors) {
      if (field.errors['required']) return `${fieldName} is required`;
      if (field.errors['min']) return `${fieldName} must be greater than ${field.errors['min'].min}`;
    }
    return '';
  }

  // Carton delete functionality
  openCartonDeleteConfirmation(carton: CartonDisplayData): void {
    this.cartonToDelete = carton;
    this.showCartonDeleteConfirmation = true;
  }

  closeCartonDeleteConfirmation(): void {
    this.showCartonDeleteConfirmation = false;
    this.cartonToDelete = null;
  }

  deleteCarton(): void {
    if (!this.cartonToDelete || !this.preAlert || this.isDeletingCarton) return;

    this.isDeletingCarton = true;
    this.error = null;

    // Call the service to delete the carton
    this.preAlertService.deleteCarton(this.preAlert.ladingBill, this.cartonToDelete.cartonNo).subscribe({
      next: (response: ApiResponse) => {
        this.isDeletingCarton = false;
        if (response.retcode === 0) {
          // Remove carton from local array
          this.cartons = this.cartons.filter(c => c.cartonNo !== this.cartonToDelete!.cartonNo);
          
          // Update pre-alert totals
          if (this.preAlert) {
            this.preAlert.totalCartons = Math.max(0, (this.preAlert.totalCartons || 0) - 1);
            // Recalculate totals if needed
            this.updatePreAlertTotals();
          }
          
          this.closeCartonDeleteConfirmation();
        } else {
          this.error = response.message || 'Failed to delete carton';
        }
      },
      error: (error: any) => {
        this.isDeletingCarton = false;
        this.error = 'Failed to delete carton';
        console.error('Delete carton error:', error);
      }
    });
  }

  private updatePreAlertTotals(): void {
    if (!this.preAlert) return;
    
    // Recalculate totals based on remaining cartons
    this.preAlert.totalCartons = this.cartons.length;
    
    // If cartons have weight/volume data, recalculate those totals
    const totalWeight = this.cartons.reduce((sum, c) => sum + (c.weight || 0), 0);
    const totalVolume = this.cartons.reduce((sum, c) => sum + (c.volume || 0), 0);
    
    if (totalWeight > 0) this.preAlert.totalWeight = totalWeight;
    if (totalVolume > 0) this.preAlert.totalVolume = totalVolume;
  }

  // ── DS token helpers ──

  getEtaVariant(eta: Date | string): StatusBadgeVariant {
    const status = this.getETAStatus(eta);
    switch (status) {
      case 'overdue':  return 'danger';
      case 'today':    return 'warning';
      case 'upcoming': return 'info';
      default:         return 'success';
    }
  }

  getEtaLabel(eta: Date | string): string {
    const days = this.getDaysUntilETA(eta);
    const status = this.getETAStatus(eta);
    if (status === 'overdue') return `${Math.abs(days)}d overdue`;
    if (status === 'today')   return 'Today';
    if (days === 1)           return 'Tomorrow';
    return `${days}d away`;
  }

  getPreAlertStatusVariant(status: string): StatusBadgeVariant {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'arrived':
      case 'completed':
      case 'delivered':  return 'success';
      case 'pending':
      case 'processing': return 'warning';
      case 'cancelled':
      case 'failed':     return 'danger';
      case 'in transit':
      case 'intransit':  return 'info';
      default:           return 'neutral';
    }
  }

  getExportStatusVariant(status: string): StatusBadgeVariant {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'failed':    return 'danger';
      case 'processing':
      case 'pending':   return 'warning';
      default:          return 'neutral';
    }
  }

  getCartonStatusClass(carton: CartonDisplayData): string {
    // You can add custom logic here based on carton status if available
    return 'text-success';
  }

  getCartonStatusIcon(carton: CartonDisplayData): string {
    // You can add custom logic here based on carton status if available
    return 'bi-box-seam';
  }
}




